import { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent';

/**
 * New Relic Browser monitoring.
 *
 * Config is supplied via Vite env vars (VITE_NEW_RELIC_*) so account
 * identifiers stay out of source control and differ per environment.
 * When the required values are absent the agent is not started — monitoring
 * degrades to a no-op instead of throwing, mirroring the backend APM design.
 *
 * Values come from New Relic → Browser → peak-d → Settings → Application settings
 * (Copy/paste JavaScript snippet):
 *   info.licenseKey         → VITE_NEW_RELIC_LICENSE_KEY
 *   info.applicationID      → VITE_NEW_RELIC_APPLICATION_ID
 *   loader_config.accountID → VITE_NEW_RELIC_ACCOUNT_ID
 *   loader_config.agentID   → VITE_NEW_RELIC_AGENT_ID   (optional; defaults to applicationID)
 *   loader_config.trustKey  → VITE_NEW_RELIC_TRUST_KEY  (optional; defaults to accountID)
 *   info.beacon             → VITE_NEW_RELIC_BEACON      (optional; defaults to bam.nr-data.net)
 */

const env = import.meta.env;

const licenseKey    = env.VITE_NEW_RELIC_LICENSE_KEY     as string | undefined;
const applicationID = env.VITE_NEW_RELIC_APPLICATION_ID  as string | undefined;
const accountID     = env.VITE_NEW_RELIC_ACCOUNT_ID      as string | undefined;
const agentID       = (env.VITE_NEW_RELIC_AGENT_ID       as string | undefined) ?? applicationID;
const trustKey      = (env.VITE_NEW_RELIC_TRUST_KEY      as string | undefined) ?? accountID;
const beacon        = (env.VITE_NEW_RELIC_BEACON         as string | undefined) ?? 'bam.nr-data.net';

let agent: BrowserAgent | null = null;

/**
 * Initialize the New Relic Browser agent. Safe to call once at startup.
 * Returns null (no-op) when VITE_NEW_RELIC_LICENSE_KEY / APPLICATION_ID / ACCOUNT_ID are unset.
 */
export function initBrowserMonitoring(): BrowserAgent | null {
  if (agent) return agent;
  if (!licenseKey || !applicationID || !accountID) return null;

  try {
    agent = new BrowserAgent({
      info: {
        beacon,
        errorBeacon: beacon,
        licenseKey,
        applicationID,
        sa: 1,
      },
      loader_config: {
        accountID,
        trustKey:      trustKey      as string,
        agentID:       agentID       as string,
        licenseKey,
        applicationID,
      },
      init: {
        distributed_tracing: { enabled: true },
        privacy:             { cookies_enabled: true },
        ajax:                { deny_list: [beacon] },
      },
    });
    return agent;
  } catch {
    agent = null;
    return null;
  }
}

/**
 * Tag the current browser session with the signed-in user so New Relic
 * Browser data (page views, JS errors, AJAX) can be filtered by user.
 * Call after login / session restore. No-op when the agent isn't running.
 */
export function setMonitoringUser(
  userId?: string | number | null,
  email?:  string | null,
): void {
  if (!agent) return;
  try {
    agent.setUserId(userId != null ? String(userId) : null);
    if (email) agent.setCustomAttribute('user_email', email);
  } catch {
    // monitoring must never throw
  }
}
